import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { ArrowUpRight, Menu, Search, X } from "lucide-react";
import { InstagramIcon, FacebookIcon } from "@/components/site/SocialIcons";
import { useLang } from "@/lib/i18n";
import { useApi } from "@/lib/api";

export const NAV = [
  { to: "/homes-and-land-for-sale", label: "nav.homes", id: "homes" },
  { to: "/for-rent", label: "nav.stay", id: "stay" },
  { to: "/areas", label: "nav.areas", id: "areas" },
  { to: "/retreats", label: "nav.retreats", id: "retreats" },
  { to: "/stories", label: "nav.stories", id: "stories" },
  { to: "/directory", label: "nav.directory", id: "directory" },
  { to: "/gallery", label: "nav.gallery", id: "gallery" },
  { to: "/about", label: "nav.about", id: "about" },
  { to: "/contact", label: "nav.contact", id: "contact" },
];

const linkCls = ({ isActive }) =>
  `whitespace-nowrap text-[12px] font-bold uppercase tracking-[0.08em] transition-colors duration-200 ${isActive ? "text-forest" : "text-ink/70 hover:text-forest"}`;

export const Logo = ({ light }) => {
  const { to, t } = useLang();
  return (
  <Link to={to("/")} className="group flex flex-col leading-none" data-testid="nav-logo">
    <span className={`font-logo text-2xl font-black tracking-tight ${light ? "text-white" : "text-forest"}`}>Escamequita</span>
    <span className={`mt-1 text-[10px] font-bold uppercase tracking-[0.32em] ${light ? "text-white/70" : "text-sage"}`}>{t("nav.country")}</span>
  </Link>
  );
};

export const LangToggle = ({ testId = "lang-toggle" }) => {
  const { lang, pathFor } = useLang();
  return (
    <div className="flex items-center rounded-full border-2 border-forest p-0.5 text-xs font-extrabold" data-testid={testId}>
      {["en", "es"].map((l) => (
        <Link key={l} to={pathFor(l)} hrefLang={l} aria-label={l === "en" ? "English" : "Español"} data-testid={`${testId}-${l}`}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors duration-200 ${lang === l ? "bg-forest text-white" : "text-forest hover:bg-forest/10"}`}>
          {l}
        </Link>
      ))}
    </div>
  );
};

export const SearchIcon = ({ testId = "nav-search" }) => {
  const { t, to } = useLang();
  return (
    <Link to={to("/search")} aria-label={t("search.label")} title={t("search.label")} data-testid={testId}
      className="grid h-9 w-9 place-items-center rounded-full text-forest transition-colors duration-200 hover:bg-forest/10">
      <Search className="h-5 w-5" />
    </Link>
  );
};

export const Socials = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <a href="https://www.instagram.com/escamequita" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="social-instagram" className="transition-opacity hover:opacity-70"><InstagramIcon /></a>
    <a href="https://www.facebook.com/Escamequita" target="_blank" rel="noreferrer" aria-label="Facebook" data-testid="social-facebook" className="transition-opacity hover:opacity-70"><FacebookIcon /></a>
  </div>
);

export const useTopBar = () => {
  const { data: s } = useApi("/settings");
  const { L } = useLang();
  return s ? { text: L(s, "topbar_text"), label: L(s, "topbar_cta_label"), url: s.topbar_cta_url } : null;
};

const TopBar = () => {
  const bar = useTopBar();
  if (!bar || (!bar.text && !(bar.label && bar.url))) return null;
  return (
    <div className="bg-forest text-white" data-testid="nav-topbar">
      <div className="container-x flex h-9 items-center justify-between gap-4 text-xs font-semibold">
        <span className="hidden truncate text-white/75 sm:inline" data-testid="nav-topbar-text">{bar.text}</span>
        {bar.label && bar.url && (
          <a href={bar.url} target="_blank" rel="noopener" data-testid="nav-cortez-link"
            className="ml-auto inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sand px-3 py-1 font-extrabold text-forest transition-colors duration-200 hover:bg-white">
            {bar.label} <ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </div>
  );
};

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { t, to } = useLang();
  return (
    <header className="sticky top-0 z-50 border-b border-clay/60 bg-white/75 backdrop-blur-xl" data-testid="site-navbar">
      <TopBar />
      <div className="container-x flex h-20 items-center justify-between gap-4">
        <Logo />
        <nav className="hidden items-center gap-3.5 xl:flex">
          {NAV.map((n) => (
            <NavLink key={n.to} to={to(n.to)} className={linkCls} data-testid={`nav-link-${n.id}`}>{t(n.label)}</NavLink>
          ))}
        </nav>
        <div className="hidden items-center gap-3 xl:flex">
          <Socials className="hidden text-forest 2xl:flex" />
          <SearchIcon />
          <LangToggle />
          <Link to={to("/contact")} className="btn-primary whitespace-nowrap !px-5 !py-2.5" data-testid="nav-cta-contact">{t("nav.tour")}</Link>
        </div>
        <div className="flex items-center gap-3 xl:hidden">
          <SearchIcon testId="nav-search-mobile" />
          <LangToggle testId="lang-toggle-mobile" />
          <button className="text-forest" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">
            {open ? <X className="h-7 w-7" /> : <Menu className="h-7 w-7" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="border-t border-clay bg-white xl:hidden" data-testid="nav-mobile-menu">
          <div className="container-x flex flex-col gap-5 py-6">
            {NAV.map((n) => (
              <NavLink key={n.to} to={to(n.to)} onClick={() => setOpen(false)} className={linkCls} data-testid={`nav-mobile-link-${n.id}`}>{t(n.label)}</NavLink>
            ))}
            <Socials className="text-forest" />
          </div>
        </div>
      )}
    </header>
  );
};
